const parseJsonObject = (value, fallback = {}) => {
  try {
    if (typeof value === "string") {
      return JSON.parse(value || "{}");
    }

    return value || fallback;
  } catch {
    return fallback;
  }
};

export const parseSaleItems = (sale) => {
  try {
    if (typeof sale?.items === "string") {
      return JSON.parse(sale.items || "[]");
    }

    return Array.isArray(sale?.items) ? sale.items : [];
  } catch {
    return [];
  }
};

export const parsePaymentBreakdown = (sale) => {
  return parseJsonObject(
    sale?.payment_breakdown ||
      sale?.paymentBreakdown ||
      sale?.payment ||
      {},
    {}
  );
};

export const isVoidSale = (sale) => {
  return sale?.is_void === true || sale?.is_void === "true";
};

export const isRefundSale = (sale) => {
  if (!sale) return false;

  if (sale.is_refund === true || sale.is_refund === "true") {
    return true;
  }

  const paymentMethod = String(
    sale.payment_method || sale.paymentMethod || ""
  ).toLowerCase();

  if (paymentMethod === "refund") {
    return true;
  }

  if (Number(sale.total || 0) < 0) {
    return true;
  }

  const items = parseSaleItems(sale);

  return items.some(
    (item) =>
      item?.is_refund === true ||
      item?.is_refund === "true" ||
      String(item?.line_type || "").toLowerCase() === "refund"
  );
};

export const getSalePaymentTotals = (sale) => {
  const breakdown = parsePaymentBreakdown(sale);
  const saleTotal = Number(sale?.total || 0);

  const cashPaid = Number(
    breakdown.cash ??
      breakdown.cashPaid ??
      breakdown.cash_paid ??
      0
  );

  const cardPaid = Number(
    breakdown.card ??
      breakdown.cardPaid ??
      breakdown.card_paid ??
      0
  );

  const change = Number(
    breakdown.change ??
      breakdown.changeDue ??
      breakdown.change_due ??
      0
  );

  let cash = cashPaid - change;
  let card = cardPaid;

  // Older transactions may not have payment_breakdown.
  if (cashPaid === 0 && cardPaid === 0) {
    const paymentMethod = String(
      sale?.payment_method || sale?.paymentMethod || ""
    ).toLowerCase();

    if (paymentMethod === "cash") {
      cash = saleTotal;
      card = 0;
    } else if (paymentMethod === "card") {
      cash = 0;
      card = saleTotal;
    }
  }

  return {
    cash,
    card,
    change,
    cashPaid,
    cardPaid,
  };
};

export const calculateBusinessSummary = (sales) => {
  const initialSummary = {
    grossSales: 0,
    refundTotal: 0,
    netSales: 0,

    cashSales: 0,
    cashReturns: 0,
    netCash: 0,

    cardSales: 0,
    cardReturns: 0,
    netCard: 0,

    saleTransactions: 0,
    refundTransactions: 0,
    totalTransactions: 0,
  };

  if (!Array.isArray(sales) || sales.length === 0) {
    return initialSummary;
  }

  const summary = sales.reduce((acc, sale) => {
    if (isVoidSale(sale)) {
      return acc;
    }

    const saleTotal = Number(sale.total || 0);
    const payment = getSalePaymentTotals(sale);
    const refund = isRefundSale(sale);

    if (refund) {
      const refundAmount = Math.abs(saleTotal);

      /*
       * Refund payment values are normally stored as negatives.
       * Math.abs(Math.min(value, 0)) safely extracts the returned amount.
       */
      const cashReturned = Math.abs(
        Math.min(Number(payment.cash || 0), 0)
      );

      const cardReturned = Math.abs(
        Math.min(Number(payment.card || 0), 0)
      );

      acc.refundTransactions += 1;
      acc.refundTotal += refundAmount;

      acc.cashReturns += cashReturned;
      acc.cardReturns += cardReturned;

      return acc;
    }

    const normalSaleTotal = Math.max(0, saleTotal);
    const cashSale = Math.max(0, Number(payment.cash || 0));
    const cardSale = Math.max(0, Number(payment.card || 0));

    acc.saleTransactions += 1;
    acc.grossSales += normalSaleTotal;
    acc.cashSales += cashSale;
    acc.cardSales += cardSale;

    return acc;
  }, initialSummary);

  summary.netSales = summary.grossSales - summary.refundTotal;
  summary.netCash = summary.cashSales - summary.cashReturns;
  summary.netCard = summary.cardSales - summary.cardReturns;

  summary.totalTransactions =
    summary.saleTransactions + summary.refundTransactions;

  return summary;
};